/**
 * App.tsx — root komponent SPA (host-aware, F7b T7b.2).
 *
 * SPA serwuje trzy scope'y z jednego buildu (D1 z PHASE_7B_PLAN):
 *  - `admin`  — super-admin panel (F6/F7a routes: /login, /dashboard, /tenants, …).
 *  - `tenant` — tenant shell (F7b+: /login, /dashboard, /profile, /settings/…).
 *  - `public` — Register, PublicLogin (F7b T7b.3/T7b.6).
 *
 * Scope rozstrzygnięty przez `useHostScope()` (hook F7b T7b.1). Każdy scope ma własny
 * auth Provider i własny set route'ów — brak współdzielenia state auth między scope'ami.
 */

// F12.A T12.A7 — rejestracja 20 typów pól w FieldRegistry (side-effect import)
import '@/components/fields/types';
// F12.H T12.H.2 — rejestracja elementów strukturalnych w LayoutElementRegistry
import '@/components/modules/layout-elements';

import { lazy, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, Route, Routes } from 'react-router-dom';

import { AdminAppearanceSync } from '@/components/AdminAppearanceSync';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { Sidebar } from '@/components/Sidebar';
import {
  ADMIN_DEFAULT_PATH,
  ADMIN_TABS_STORAGE_KEY,
  resolveAdminTabMeta,
} from '@/components/tabs/admin-tab-meta';
import { AdminTabPanels } from '@/components/tabs/AdminTabPanels';
import { TabShellContext } from '@/components/tabs/tab-shell';
import { TabBar } from '@/components/tabs/TabBar';
import {
  resolveTenantTabMeta,
  TENANT_DEFAULT_PATH,
  TENANT_TABS_STORAGE_KEY,
} from '@/components/tabs/tenant-tab-meta';
import { TenantTabPanels } from '@/components/tabs/TenantTabPanels';
import { TenantLayout } from '@/components/tenant/TenantLayout';
import { TenantAppearanceSync } from '@/components/TenantAppearanceSync';
import { Topbar } from '@/components/Topbar';
import { AuthProvider } from '@/contexts/AuthContext';
import { TabProvider, useTabs } from '@/contexts/TabContext';
import { TenantAuthProvider } from '@/contexts/TenantAuthContext';
import { useHostScope } from '@/hooks/useHostScope';
import { useTenantUpdating } from '@/lib/tenant-updating';
import { AcceptInvite } from '@/pages/AcceptInvite';
import { Admin2FASetup } from '@/pages/Admin2FASetup';
import { AdminAcceptInvite } from '@/pages/AdminAcceptInvite';
import { AdminForgotPassword } from '@/pages/AdminForgotPassword';
import { AdminResetPassword } from '@/pages/AdminResetPassword';
import { ForgotPassword } from '@/pages/ForgotPassword';
import { HostMisconfigured } from '@/pages/HostMisconfigured';
import { Login } from '@/pages/Login';
import { PublicLogin } from '@/pages/PublicLogin';
import { Register } from '@/pages/Register';
import { ResetPassword } from '@/pages/ResetPassword';
import { TenantLogin } from '@/pages/TenantLogin';
import { TenantSelect } from '@/pages/TenantSelect';
import { TenantUpdating } from '@/pages/TenantUpdating';

// F17 T17.1.5 — lazy-load buildera procesów. Reactflow + dagre (~80-100kb gz)
// nie ląduje w głównym chunku admin SPA — tylko admin który wejdzie w
// `/processes/builder/:id` pobiera. Pierwszy lazy route w admin SPA.
const ProcessBuilderPage = lazy(() => import('@/pages/processes/ProcessBuilderPage'));

// F17 T17.11.5 — lazy-load buildera wzorów (admin scope). Dzieli chunk Reactflow
// z builderem tenanta — przeglądarka pobiera tylko przy pierwszym wejściu.
const AdminProcessTemplateBuilderPage = lazy(
  () => import('@/pages/admin-process-templates/AdminProcessTemplateBuilderPage'),
);

/**
 * Suspense fallback dla lazy ProcessBuilderPage. Wydzielony do osobnego
 * komponentu żeby mieć dostęp do `useTranslation` (Suspense.fallback nie
 * może być fragmentem z hookiem inline w props).
 */
function ProcessBuilderLoadingFallback() {
  const { t } = useTranslation();
  return (
    <div className="flex h-screen items-center justify-center bg-bg-primary">
      <div className="text-text-secondary">{t('tenant.processes.builder.loading')}</div>
    </div>
  );
}

export function App() {
  const scope = useHostScope();

  if (scope === 'tenant') return <TenantApp />;
  if (scope === 'public') return <PublicApp />;
  if (scope === 'admin') return <AdminApp />;
  // F12.N D1 (2026-05-15): fallback `unknown` (np. tenant URL bez `.app.` w środku).
  // Zamiast pokazać panel ITE userowi który wpisał zły URL, renderujemy informacyjną stronę.
  return <HostMisconfigured />;
}

// Topbar powłoki admina — tytuł = etykieta aktywnej karty (pod TabProvider).
function AdminShellTopbar() {
  const { tabs, activeTabId } = useTabs();
  const active = tabs.find((t) => t.id === activeTabId);
  return <Topbar title={active?.label} />;
}

function AdminShell() {
  // Powłoka admina z kartami. Chrome (Sidebar + Topbar + pasek kart) rysuje się RAZ; strony
  // renderują własne `<Layout>`, które w powłoce (TabShellContext=true) daje tylko `<main>`.
  return (
    <TabProvider
      storageKey={ADMIN_TABS_STORAGE_KEY}
      defaultPath={ADMIN_DEFAULT_PATH}
      resolveTabMeta={resolveAdminTabMeta}
    >
      <TabShellContext.Provider value={true}>
        <div className="flex h-screen overflow-hidden bg-bg-secondary" data-testid="admin-layout">
          <Sidebar />
          <div className="flex min-w-0 flex-1 flex-col">
            <AdminShellTopbar />
            <TabBar />
            <div className="relative min-h-0 min-w-0 flex-1">
              <AdminTabPanels />
            </div>
          </div>
        </div>
      </TabShellContext.Provider>
    </TabProvider>
  );
}

function AdminApp() {
  return (
    <AuthProvider>
      <AdminAppearanceSync />
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/admin/2fa/setup" element={<Admin2FASetup />} />
        {/* ADR-0022: pre-login flows admin scope (public, bez ProtectedRoute). */}
        <Route path="/forgot-password" element={<AdminForgotPassword />} />
        <Route path="/reset-password/:token" element={<AdminResetPassword />} />
        <Route path="/accept-invite/:token" element={<AdminAcceptInvite />} />
        {/* F17 T17.11.5 — pełnoekranowy builder wzoru procesu (lazy). Poza powłoką kart. */}
        <Route
          path="/process-templates/builder/:id"
          element={
            <ProtectedRoute>
              <Suspense fallback={<ProcessBuilderLoadingFallback />}>
                <AdminProcessTemplateBuilderPage />
              </Suspense>
            </ProtectedRoute>
          }
        />
        {/* Powłoka admina z kartami — obsługuje WSZYSTKIE pozostałe trasy chronione.
            Tabela tras żyje w `AdminTabPanels`. */}
        <Route
          path="/*"
          element={
            <ProtectedRoute>
              <AdminShell />
            </ProtectedRoute>
          }
        />
      </Routes>
    </AuthProvider>
  );
}

function TenantShell() {
  // Powłoka tenanta z kartami (zakładkami). TabProvider trzyma stan kart PONAD routingiem,
  // więc przeżywa nawigację; TenantTabPanels renderuje wszystkie karty (keep-alive).
  return (
    <TabProvider
      storageKey={TENANT_TABS_STORAGE_KEY}
      defaultPath={TENANT_DEFAULT_PATH}
      resolveTabMeta={resolveTenantTabMeta}
    >
      <TenantLayout>
        <TenantTabPanels />
      </TenantLayout>
    </TabProvider>
  );
}

function TenantApp() {
  // ADR-0113b §5: baza firmy w trakcie aktualizacji (API 503 TENANT_UPDATING) — ekran „Trwa
  // aktualizacja systemu” zamiast tras (także logowania); sesja zostaje, „Odśwież” wraca do pracy.
  const updating = useTenantUpdating();
  if (updating) return <TenantUpdating />;
  return (
    <TenantAuthProvider>
      <TenantAppearanceSync />
      <Routes>
        <Route path="/login" element={<TenantLogin />} />
        {/* T10.11: pre-login flows — bez TenantLayout (user nie zalogowany). */}
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/accept-invite/:token" element={<AcceptInvite />} />
        {/* F17 T17.1.5 — pełnoekranowy builder procesu (lazy, Reactflow). Świadomie POZA
            powłoką kart — bez sidebara/zakładek (fokus na edycji grafu). */}
        <Route
          path="/processes/builder/:id"
          element={
            <Suspense fallback={<ProcessBuilderLoadingFallback />}>
              <ProcessBuilderPage />
            </Suspense>
          }
        />
        {/* Powłoka tenanta z kartami — obsługuje WSZYSTKIE pozostałe trasy post-login.
            Tabela tras żyje w `TenantTabPanels` (każda karta z własnym routingiem). */}
        <Route path="/*" element={<TenantShell />} />
      </Routes>
    </TenantAuthProvider>
  );
}

function PublicApp() {
  return (
    <Routes>
      {/* T7b.3: /login (email-only lookup) + /select (multi-tenant chooser). */}
      {/* T7b.6: /register (3-step wizard + spinner view + error/retry). */}
      <Route path="/login" element={<PublicLogin />} />
      <Route path="/select" element={<TenantSelect />} />
      <Route path="/register" element={<Register />} />
      {/* T10.11: forgot/reset bez slug-a (rozsylka per tenant gdzie konto istnieje). */}
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/" element={<Navigate to="/login" replace />} />
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
}

export default App;
